import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardSection,
  Gutter,
  EmptyLayout,
  SmallHeader,
  Paragraph,
  useApi,
  ToastUtil,
  CondensedHeader,
  CondensedLarge,
} from '@axiom/ui';
import type {
  AfcSubmissionCandidate,
  AfcSubmissionCandidateOpportunity,
  GroupedExperiences,
} from '@axiom/validation';
import { useNavigate, useParams } from 'react-router';
import { AccountConst } from '@axiom/const';

import FaceCardsGraphic from '../../public/svg/face-cards.svg';
import { AccountSubmissionsLegacyApi } from '../../api/account-submissions-legacy-api';
import { CandidateApi } from '../../api/candidate-api';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { CandidateCardHeader } from '../CandidateCardHeader/CandidateCardHeader';
import { EngagementPrompt } from '../EngagementPrompt/EngagementPrompt';
import { AccountLegacyApi } from '../../api/account-legacy-api';
import { useEngagementInterviewTalentData } from '../../hooks/useEngagementInterviewTalentData';
import type { EngagementPassModalForm } from '../EngagementPassModal/EngagementPassModal';
import { EngagementPassModal } from '../EngagementPassModal/EngagementPassModal';
import { InstantMatchesApi } from '../../api/instant-matches-api';
import { ClientRequestApi } from '../../api/client-request-api';
import { SubmissionApi } from '../../api/submission-api';

import { MyEngagementsInterviewTalentProfile } from './MyEngagementsInterviewTalentProfile';

export const MyEngagementsItemInterviewTalent = () => {
  const navigate = useNavigate();
  const [showEngageConfirmPrompt, setShowEngageConfirmPrompt] =
    useState<AfcSubmissionCandidateOpportunity | null>();
  const [showPassConfirmPrompt, setShowPassConfirmPrompt] =
    useState<AfcSubmissionCandidate | null>();
  const params = useParams();
  const { accountId, submissionId } = params;

  if (!accountId) {
    throw new Error('accountId was not provided');
  }

  if (!submissionId) {
    throw new Error('submissionId was not provided');
  }

  const { candidateOpps, currentInterviewSubmission } =
    useEngagementInterviewTalentData({ submissionId });

  const allGroupedExperiences = useApi(
    ...candidateOpps.map(({ candidate: { id } }) =>
      CandidateApi.readCandidateGroupedExperiences(id, {
        submissionId,
      })
    )
  ).map(response => response.data);

  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());

  const handleEngageClick = (
    candidateOpp?: AfcSubmissionCandidateOpportunity | null
  ) => {
    setShowEngageConfirmPrompt(candidateOpp);
  };
  const handlePassClick = (
    candidateOpp: AfcSubmissionCandidateOpportunity | null
  ) => {
    setShowPassConfirmPrompt(candidateOpp?.candidate);
  };

  const refreshData = async (
    candidate?: AfcSubmissionCandidateOpportunity['candidate']
  ) => {
    await Promise.all([
      AccountLegacyApi.refreshAccount(accountId, {
        expand: [AccountConst.Expansions.accountTeamUserInfo],
      }),
      SubmissionApi.refreshSubmissionDetails(submissionId),
      AccountSubmissionsLegacyApi.refreshCandidateAccountSubmissionDetails(
        accountId,
        submissionId,
        candidate?.id ?? ''
      ),
      InstantMatchesApi.refreshInstantMatches(submissionId),
      SubmissionApi.refreshSubmissionDetails(submissionId),
    ]);
  };

  const handleSubmitPassModal = async (
    formData: EngagementPassModalForm,
    candidate: AfcSubmissionCandidateOpportunity['candidate']
  ) => {
    setShowPassConfirmPrompt(null);
    await ClientRequestApi.createPassOnCandidateRequest({
      submissionId,
      candidateId: candidate.id,
      rejectionLossCodes: [formData.rejectionLossCode],
      notes: formData.notes,
    });
    void refreshData(candidate);
  };

  const loadFailureToast = () => {
    ToastUtil.add({
      name: `FAILED_INTERVIEW_OR_ENGAGE_REQUEST`,
      type: 'error',
      dismissible: true,
      children: (
        <>
          <CondensedHeader>Failed to Interview/Engage.</CondensedHeader>
          <CondensedLarge>Please try again later.</CondensedLarge>
        </>
      ),
    });
  };

  const handleOnConfirmClick = async (
    candidate: AfcSubmissionCandidateOpportunity['candidate']
  ) => {
    setShowEngageConfirmPrompt(null);
    try {
      await ClientRequestApi.createEngagementRequest({
        submissionId,
        candidateId: candidate.id,
      });
      void refreshData(candidate);
      void navigate('../matches');
    } catch {
      loadFailureToast();
    }
  };

  return (
    <div data-test="interview-talent-tab">
      {candidateOpps.length === 0 && (
        <Card name="INTERVIEW_EMPTY_CARD">
          <CardSection>
            <EmptyLayout
              graphic={
                <FaceCardsGraphic
                  width="117px"
                  height="91px"
                  data-test="face-cards-graphic"
                />
              }
              graphicPosition="left"
            >
              <SmallHeader>Upcoming interviews will appear here</SmallHeader>
              <Gutter bottom="16px" />
              <Paragraph>
                Let us know you want to interview talent on the matches tab.
                Upcoming interviews will appear here where you can provide
                feedback and continue to move them through the hiring process.
              </Paragraph>
            </EmptyLayout>
          </CardSection>
        </Card>
      )}
      {candidateOpps.map((candidateOpp, index) => {
        return (
          <Gutter
            bottom="16px"
            key={`interview-${candidateOpp.id}-${index + 1}`}
          >
            <Card>
              <CardHeader>
                <CandidateCardHeader
                  candidateOpp={candidateOpp}
                  onEngage={() => {
                    handleEngageClick(candidateOpp);
                  }}
                  submission={currentInterviewSubmission}
                  onPass={() => {
                    handlePassClick(candidateOpp);
                  }}
                />
              </CardHeader>
              <CardSection>
                <MyEngagementsInterviewTalentProfile
                  candidate={candidateOpp.candidate}
                  groupedExperiences={
                    allGroupedExperiences[index] as GroupedExperiences[]
                  }
                  submission={currentInterviewSubmission}
                  taxonomy={taxonomy}
                />
              </CardSection>
            </Card>
          </Gutter>
        );
      })}
      {!!showPassConfirmPrompt && (
        <EngagementPassModal
          candidate={showPassConfirmPrompt}
          closeModal={() => {
            handlePassClick(null);
          }}
          submitModal={async data =>
            handleSubmitPassModal(data, showPassConfirmPrompt)
          }
        />
      )}
      {!!showEngageConfirmPrompt && (
        <EngagementPrompt
          candidateOpp={showEngageConfirmPrompt}
          onClose={() => {
            handleEngageClick(null);
          }}
          onConfirm={handleOnConfirmClick}
        />
      )}
    </div>
  );
};
